import { Vector } from './Vector'
import { Plane } from './Plane'
import { Grid, GridObject } from './Grid'
import * as PIXI from 'pixi.js'
import type { IDebugRenderable, IRenderable } from './GameObject'

// 1 = 1 mile so 1/5280 is 1 foot
const oneFoot = 1/5280
const oneInch = oneFoot / 12;

export class GridRenderable<T extends GridObject = GridObject> {
    private readonly debugGraphics: PIXI.Graphics; //();
    private readonly lineWidth = oneInch*6;
    private readonly GRID_LINE_COLOR = 0x666666;
    private readonly grid: Grid<T>;

    constructor(debugGraphics: PIXI.Graphics, grid: Grid<T>) {
        this.debugGraphics = debugGraphics;
        this.grid = grid;
    }

    update(isDebugEnabled: boolean) {
        if (isDebugEnabled) {
            const left = this.grid.location.x - this.grid.width / 2;
            const top = this.grid.location.y - this.grid.height / 2;
            const right = left + this.grid.columns * this.grid.cellSize;
            const bottom = top + this.grid.rows * this.grid.cellSize;

            for (let col = 0; col <= this.grid.columns; col++) {
                const x = left + col * this.grid.cellSize;
                this.debugGraphics
                    .moveTo(x, top)
                    .lineTo(x, bottom)
                    .stroke({ width: this.lineWidth, color: this.GRID_LINE_COLOR });
            }

            for (let row = 0; row <= this.grid.rows; row++) {
                const y = top + row * this.grid.cellSize;
                this.debugGraphics
                    .moveTo(left, y)
                    .lineTo(right, y)
                    .stroke({ width: this.lineWidth, color: this.GRID_LINE_COLOR });
            }
        }
    }
}

export class PlaneRenderable implements IDebugRenderable {
    private readonly debugGraphics: PIXI.Graphics;
    // Note: worldContainer is scaled by 4000x, so line widths must be tiny!
    private readonly lineWidth = oneInch*6; // 0.0005;  // Will be scaled to ~2 pixels on screen
    private readonly normalLength = oneFoot*3; // 0.010;
    private readonly arrowSize = this.normalLength * 0.3;  // Arrowhead is 30% of normal length
    private readonly angle = Math.PI / 6;  // 30 degrees
    private readonly plane: Plane;

    constructor(debugGraphics: PIXI.Graphics, plane: Plane) {
        this.debugGraphics = debugGraphics;
        this.plane = plane;
    }

    drawArrowhead(tipX: number, tipY: number, normalX: number, normalY: number, color: number) {

        // Rotate the reverse normal by ±30 degrees to get arrowhead lines
        const reverseX = -normalX;
        const reverseY = -normalY;

        // Right side of arrowhead (rotate by +30°)
        const right1X = reverseX * Math.cos(this.angle) - reverseY * Math.sin(this.angle);
        const right1Y = reverseX * Math.sin(this.angle) + reverseY * Math.cos(this.angle);

        // Left side of arrowhead (rotate by -30°)
        const left1X = reverseX * Math.cos(-this.angle) - reverseY * Math.sin(-this.angle);
        const left1Y = reverseX * Math.sin(-this.angle) + reverseY * Math.cos(-this.angle);

        // Draw both arrowhead lines
        this.debugGraphics
            .moveTo(tipX, tipY)
            .lineTo(tipX + right1X * this.arrowSize, tipY + right1Y * this.arrowSize)
            .stroke({ width: this.lineWidth, color: color });

        this.debugGraphics
            .moveTo(tipX, tipY)
            .lineTo(tipX + left1X * this.arrowSize, tipY + left1Y * this.arrowSize)
            .stroke({ width: this.lineWidth, color: color });
    };

    debug(position: Vector) {
        const topClosest = Plane.closestPointOnPlane(this.plane, position);
        // Draw top boundary plane normal (green arrow)
        const topTipX = topClosest.x + this.plane.normal.x * this.normalLength;
        const topTipY = topClosest.y + this.plane.normal.y * this.normalLength;
        this.debugGraphics
            .moveTo(topClosest.x, topClosest.y)
            .lineTo(topTipX, topTipY)
            .stroke({ width: this.lineWidth, color: 0x00ff00 });
        this.drawArrowhead(topTipX, topTipY, this.plane.normal.x, this.plane.normal.y, 0x00ff00);
    }
}

export class CenteredSpriteWithHalfExtents {
    readonly container: PIXI.Container;

    // each component is the half extent of the AABB
    // the magnitude of this vector represents the longest distance 
    // from the center of the object to its edge
    readonly radius: Vector;
    readonly sprite: PIXI.Sprite;

    constructor(container: PIXI.Container, imagePath: string, width: number = 0.01, height: number | null = 0.01) {
        this.container = container;
        console.log(`creating Renderable with image: ${imagePath} and width: ${width} and height: ${height}`);
        const texture = PIXI.Assets.get(imagePath);
        if (!texture) {
            console.error('Texture not found for:', imagePath);
            throw new Error(`Texture not loaded: ${imagePath}`);
        }
        this.sprite = new PIXI.Sprite(texture);
        this.sprite.anchor.set(0.5);
        if (height == null) {
            this.setWidthKeepingAspectRatio(width);
        } else {
            this.setWidthHeight(width, height);
        }
        this.radius = new Vector(this.sprite.width / 2, this.sprite.height / 2, 0);
        this.container.addChild(this.sprite);
    }

    private setWidthKeepingAspectRatio(width: number) {
        const scale = width / this.sprite.texture.width;
        this.sprite.width = width;
        this.sprite.height = this.sprite.texture.height * scale;
    }

    private setWidthHeight(width: number, height: number) {
        this.sprite.width = width;
        this.sprite.height = height;
    }
}

export class TriangularBarrierRenderable implements IRenderable {
    private readonly graphics: PIXI.Graphics;
    private readonly debugGraphics: PIXI.Graphics;
    readonly canCollide: boolean;
    readonly radius: Vector;
    private readonly width: number;
    private readonly height: number;
    private readonly orientation: 'left' | 'right' | 'top' | 'bottom';  // Direction the apex points
    private readonly fillColor: number;
    readonly sprite: CenteredSpriteWithHalfExtents;

    constructor(
        // container: PIXI.Container,
        sprite: CenteredSpriteWithHalfExtents,
        debugGraphics: PIXI.Graphics,
        //width: number,
        //height: number,
        orientation: 'left' | 'right' | 'top' | 'bottom',
        canCollide: boolean,
        fillColor: number = 0x808080
    ) {
        this.sprite = sprite;
        this.graphics = new PIXI.Graphics();
        this.debugGraphics = debugGraphics;
        this.width = sprite.sprite.width; // width;
        this.height = sprite.sprite.height; // height;
        this.orientation = orientation;
        this.canCollide = canCollide;
        this.fillColor = fillColor;
        
        // Half extents for AABB collision detection
        this.radius = sprite.radius;
        
        // Draw the triangle shape
        this.drawTriangle();
        this.sprite.container.addChild(this.graphics);
        this.sprite.container.removeChild(this.sprite.sprite);
    }

    private drawTriangle() {
        this.graphics.clear();
        
        switch (this.orientation) {
            case 'left':
                // < shape: point on left, flat edge on right
                this.graphics.poly([
                    -this.width / 2, 0,                // Left point (apex)
                    this.width / 2, -this.height / 2,  // Top right
                    this.width / 2, this.height / 2    // Bottom right
                ]);
                break;
            case 'right':
                // > shape: flat edge on left, point on right
                this.graphics.poly([
                    -this.width / 2, -this.height / 2, // Top left
                    -this.width / 2, this.height / 2,  // Bottom left
                    this.width / 2, 0                  // Right point (apex)
                ]);
                break;
            case 'top':
                // \ shape: slanted barrier from top-left to bottom-right (single plane)
                // Right triangle with hypotenuse as the barrier surface
                this.graphics.poly([
                    -this.width / 2, -this.height / 2, // Top left
                    this.width / 2, -this.height / 2,  // Top right
                    this.width / 2, this.height / 2    // Bottom right
                ]);
                break;
            case 'bottom':
                // / shape: slanted barrier from bottom-left to top-right (single plane)
                // Right triangle with hypotenuse as the barrier surface
                this.graphics.poly([
                    this.width / 2, -this.height / 2,  // Top right
                    this.width / 2, this.height / 2,   // Bottom right
                    -this.width / 2, this.height / 2   // Bottom left
                ]);
                break;
        }
        
        this.graphics.fill({ color: this.fillColor });
    }

    update(position: Vector, _heading?: Vector) {
        this.graphics.x = position.x;
        this.graphics.y = position.y;
    }

    debug(position: Vector) {
        if (this.canCollide) {
            const lineWidth = oneInch * 3;
            const markerSize = Math.max(this.radius.x, this.radius.y) * 0.3;
            
            // Draw center cross marker
            this.debugGraphics
                .moveTo(position.x - markerSize, position.y)
                .lineTo(position.x + markerSize, position.y)
                .stroke({ width: lineWidth * 2, color: 0x00ffff });

            this.debugGraphics
                .moveTo(position.x, position.y - markerSize)
                .lineTo(position.x, position.y + markerSize)
                .stroke({ width: lineWidth * 2, color: 0x00ffff });

            // Draw AABB collision bounds
            this.debugGraphics
                .rect(
                    position.x - this.radius.x,
                    position.y - this.radius.y,
                    this.radius.x * 2,
                    this.radius.y * 2
                )
                .stroke({ width: lineWidth, color: 0x00ffff });
        }
    }
}

export class SpriteRenderable implements IRenderable {
    private readonly debugGraphics: PIXI.Graphics;
    readonly sprite: CenteredSpriteWithHalfExtents | null;
    readonly canCollide: boolean;
    readonly radius: Vector;
    // each component is the half extent of the AABB
    // the magnitude of this vector represents the longest distance 
    // from the center of the object to its edge

    constructor(radius: Vector, debugGraphics: PIXI.Graphics, canCollide: boolean);
    constructor(sprite: CenteredSpriteWithHalfExtents, debugGraphics: PIXI.Graphics, canCollide: boolean);
    constructor(first: CenteredSpriteWithHalfExtents | Vector, debugGraphics: PIXI.Graphics, canCollide: boolean) {
        if (first instanceof CenteredSpriteWithHalfExtents) {
            this.sprite = first;
            this.radius = first.radius;
        } else {
            this.radius = first;
            this.sprite = null;
        }

        this.debugGraphics = debugGraphics;
        this.canCollide = canCollide;
    }

    update(position: Vector, heading?: Vector) {
        if (this.sprite != null) {
            this.sprite.sprite.x = position.x;
            this.sprite.sprite.y = position.y;

            if (heading) {
                const headingMagnitudeSquared = (heading.x * heading.x) + (heading.y * heading.y);
                if (headingMagnitudeSquared > 1e-12) {
                    this.sprite.sprite.rotation = Math.atan2(heading.y, heading.x);
                }

            }
        }
    }

    debug(position: Vector) {
        if (this.canCollide) {
            const maxHalfExtent = Math.max(this.radius.x, this.radius.y);
            const lineWidth = oneInch*3; // oneFoot; // maxHalfExtent / 10;
            const markerSize = maxHalfExtent * 0.3;  // Size of the cross, scaled to the object size
            this.debugGraphics
                .moveTo(position.x - markerSize, position.y)
                .lineTo(position.x + markerSize, position.y)
                .stroke({ width: lineWidth * 2, color: 0x00ffff });  // Horizontal line

            this.debugGraphics
                .moveTo(position.x, position.y - markerSize)
                .lineTo(position.x, position.y + markerSize)
                .stroke({ width: lineWidth * 2, color: 0x00ffff });  // Vertical line

            // Draw collision AABB around object position using half extents
            this.debugGraphics
                .rect(
                    position.x - this.radius.x,
                    position.y - this.radius.y,
                    this.radius.x * 2,
                    this.radius.y * 2
                )
                .stroke({ width: lineWidth, color: 0x00ffff });
        }
    }
}
